import { ModalForm } from '@ant-design/pro-components';
import { Checkbox, Empty, Input, Spin, message } from 'antd';
import { createStyles } from 'antd-style';
import React, { useEffect, useMemo, useState } from 'react';
import { getTemplateSignList } from '@/services/sign-template/template-sign/api';
import { getMyTodayDuty, saveTodayDuty } from '@/services/sign-template/duty/api';

const useStyles = createStyles(({ token }) => ({
  searchWrap: {
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  onlySelected: {
    whiteSpace: 'nowrap',
  },
  list: {
    maxHeight: 400,
    overflowY: 'auto',
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    padding: '4px 0',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '10px 16px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: token.colorBgTextHover,
    },
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: 14,
    fontWeight: 500,
    color: token.colorTextHeading,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  desc: {
    marginTop: 2,
    fontSize: 12,
    color: token.colorTextSecondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  loading: {
    padding: '40px 0',
    textAlign: 'center',
  },
  summary: {
    marginTop: 8,
    fontSize: 12,
    color: token.colorTextSecondary,
  },
}));

type DutyModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const DutyModal: React.FC<DutyModalProps> = ({ open, onOpenChange }) => {
  const { styles } = useStyles();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<TemplateSignAPI.TemplateSignVO[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [keyword, setKeyword] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setOnlySelected(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [listRes, dutyRes] = await Promise.all([
          getTemplateSignList({ current: 1, pageSize: 200 }),
          getMyTodayDuty(),
        ]);
        if (cancelled) return;
        const dutyIds = new Set<number>(dutyRes?.data ?? []);
        // 已选模板置顶，仅在打开弹窗时排一次序，勾选过程中列表不重排
        const sorted = [...(listRes?.data ?? [])].sort(
          (a, b) => Number(dutyIds.has(b.id!)) - Number(dutyIds.has(a.id!)),
        );
        setTemplates(sorted);
        setSelectedIds(dutyIds);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filteredTemplates = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return templates.filter((t) => {
      if (onlySelected && !selectedIds.has(t.id!)) return false;
      if (!kw) return true;
      const name = (t.name ?? '').toLowerCase();
      const desc = (t.desc ?? '').toLowerCase();
      return name.includes(kw) || desc.includes(kw);
    });
  }, [keyword, templates, onlySelected, selectedIds]);

  const toggleId = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFinish = async () => {
    const res = await saveTodayDuty({ templateIds: Array.from(selectedIds) });
    if (res?.success) {
      message.success('值班配置已保存');
      return true;
    }
    message.error(res?.message || '保存失败');
    return false;
  };

  return (
    <ModalForm
      title="我要值班"
      open={open}
      onOpenChange={onOpenChange}
      width={560}
      onFinish={handleFinish}
      modalProps={{ destroyOnClose: true, okText: '确定', cancelText: '取消' }}
    >
      <div className={styles.searchWrap}>
        <Input.Search
          placeholder="搜索模板名称或描述"
          allowClear
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Checkbox
          className={styles.onlySelected}
          checked={onlySelected}
          onChange={(e) => setOnlySelected(e.target.checked)}
        >
          只看已选（{selectedIds.size}）
        </Checkbox>
      </div>
      <div className={styles.list}>
        {loading ? (
          <div className={styles.loading}>
            <Spin />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              keyword ? '未匹配到模板' : onlySelected ? '暂无已选模板' : '暂无可值班的模板'
            }
            style={{ padding: '24px 0' }}
          />
        ) : (
          filteredTemplates.map((t) => {
            const id = t.id!;
            const checked = selectedIds.has(id);
            return (
              <div key={id} className={styles.row} onClick={() => toggleId(id)}>
                <Checkbox
                  checked={checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleId(id)}
                />
                <div className={styles.rowText}>
                  <div className={styles.name}>{t.name || '未命名模板'}</div>
                  {t.desc && <div className={styles.desc}>{t.desc}</div>}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className={styles.summary}>已选 {selectedIds.size} 个模板，值班有效期至今日 24:00</div>
    </ModalForm>
  );
};

export default DutyModal;
